import { c as _c } from "react/compiler-runtime";
import { IconInfoCircle } from "@tabler/icons-react";
import { Card, CardContent, CardDescription, CardTitle } from "@domainstack/ui/card";
import { Icon } from "@domainstack/ui/icon";
import { ResponsiveTooltip, ResponsiveTooltipContent, ResponsiveTooltipTrigger } from "@domainstack/ui/responsive-tooltip";
import { Spinner } from "@domainstack/ui/spinner";
import { cn } from "@domainstack/ui/utils";
const GLOW_CLASSES = {
  blue: "glow-accent-blue/15",
  purple: "glow-accent-purple/15",
  green: "glow-accent-green/15",
  orange: "glow-accent-orange/15",
  pink: "glow-accent-pink/15",
  cyan: "glow-accent-cyan/15",
  slate: "glow-accent-slate/15"
};
interface ReportSectionProps {
  title: string;
  description?: string;
  help?: string;
  icon?: React.ElementType;
  slug?: string;
  accent?: keyof typeof GLOW_CLASSES;
  isLoading?: boolean;
  /** Optional actions to render in the top-right of the section header */
  headerActions?: React.ReactNode;
  children?: React.ReactNode;
}
export function ReportSection(t0) {
  const $ = _c(46);
  const {
    title,
    description,
    help,
    icon,
    slug,
    accent: t1,
    isLoading,
    headerActions,
    children
  } = t0;
  const accent = t1 === undefined ? "slate" : t1;
  const IconComponent = icon;
  const t2 = slug ?? title;
  let t3;
  if ($[0] !== t2) {
    let t4;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = /[^a-z0-9-]/g;
      $[2] = t4;
    } else {
      t4 = $[2];
    }
    t3 = t2.toLowerCase().replace(/\s+/g, "-").replace(t4, "");
    $[0] = t2;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const computedSlug = t3;
  const headerId = `section-header-${computedSlug}`;
  const contentId = `section-content-${computedSlug}`;
  const t4 = GLOW_CLASSES[accent];
  let t5;
  if ($[3] !== t4) {
    t5 = cn("pointer-events-none absolute -inset-x-20 -top-20 h-52", t4);
    $[3] = t4;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] !== t5) {
    t6 = <div aria-hidden={true} className={t5} />;
    $[5] = t5;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== IconComponent) {
    t7 = IconComponent && <Icon size="sm" className="rounded-full"><IconComponent /></Icon>;
    $[7] = IconComponent;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== title) {
    t8 = <span className="text-base">{title}</span>;
    $[9] = title;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== help || $[12] !== title) {
    t9 = help && <ResponsiveTooltip><ResponsiveTooltipTrigger render={<button type="button" aria-label={`More info about ${title}`} className="-m-1 inline-flex cursor-help rounded-sm p-1 outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"><IconInfoCircle className="size-3.5 opacity-60" aria-hidden={true} /></button>} /><ResponsiveTooltipContent>{help}</ResponsiveTooltipContent></ResponsiveTooltip>;
    $[11] = help;
    $[12] = title;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== t8 || $[15] !== t9) {
    t10 = <CardTitle className="flex items-center gap-2">{t8}{t9}</CardTitle>;
    $[14] = t8;
    $[15] = t9;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== description || $[18] !== help) {
    t11 = (description || help) && <CardDescription className="sr-only">{description}</CardDescription>;
    $[17] = description;
    $[18] = help;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== t10 || $[21] !== t11) {
    t12 = <div className="min-w-0 flex-1">{t10}{t11}</div>;
    $[20] = t10;
    $[21] = t11;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== isLoading) {
    t13 = isLoading && <div className="mr-2 flex items-center gap-2 text-xs text-muted-foreground"><Spinner className="size-5" /><span className="sr-only">Loading</span></div>;
    $[23] = isLoading;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] !== headerActions || $[26] !== t13) {
    t14 = <div className="ml-auto flex items-center gap-3">{t13}{headerActions}</div>;
    $[25] = headerActions;
    $[26] = t13;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] !== t12 || $[29] !== t14 || $[30] !== t7) {
    t15 = <div className="flex w-full items-center gap-2 text-left">{t7}{t12}{t14}</div>;
    $[28] = t12;
    $[29] = t14;
    $[30] = t7;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  let t16;
  if ($[32] !== headerId || $[33] !== t15) {
    t16 = <div className="relative"><div className="p-5" id={headerId}>{t15}</div></div>;
    $[32] = headerId;
    $[33] = t15;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  let t17;
  if ($[35] !== children || $[36] !== contentId) {
    t17 = children && <div id={contentId} className="relative"><CardContent className="space-y-2 px-5 pt-0 pb-5">{children}</CardContent></div>;
    $[35] = children;
    $[36] = contentId;
    $[37] = t17;
  } else {
    t17 = $[37];
  }
  let t18;
  if ($[38] !== t16 || $[39] !== t17 || $[40] !== t6) {
    t18 = <Card className="relative gap-0 overflow-hidden py-0">{t6}{t16}{t17}</Card>;
    $[38] = t16;
    $[39] = t17;
    $[40] = t6;
    $[41] = t18;
  } else {
    t18 = $[41];
  }
  let t19;
  if ($[42] !== computedSlug || $[43] !== headerId || $[44] !== t18) {
    t19 = <section id={computedSlug} aria-labelledby={headerId} className="scroll-mt-[calc(var(--section-nav-height)+var(--scroll-padding))] border-none md:scroll-mt-[calc(var(--header-height)+var(--section-nav-height)+var(--scroll-padding))]">{t18}</section>;
    $[42] = computedSlug;
    $[43] = headerId;
    $[44] = t18;
    $[45] = t19;
  } else {
    t19 = $[45];
  }
  return t19;
}